import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompare } from '@/contexts/CompareContext';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Check, CheckCircle, XCircle, Shield } from 'lucide-react';

const PolicyDetailPage = () => {
  const { id } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCompare, isInCompare } = useCompare();

  useEffect(() => {
    fetchPolicy();
  }, [id]);

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/policies/${id}`);
      setPolicy(response.data);
    } catch (error) {
      console.error('Failed to fetch policy:', error);
      toast.error('Failed to load policy details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50" data-testid="policy-detail-page-loading">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
          <Skeleton className="h-12 w-1/3 mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-64" />
              <Skeleton className="h-96" />
            </div>
            <Skeleton className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <Card>
          <CardContent className="py-12 text-center">
            <h2 className="text-2xl font-semibold text-secondary-900 mb-4">Policy not found</h2>
            <Link to="/policies">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Policies
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50" data-testid="policy-detail-page">
      {/* Header */}
      <div className="bg-white border-b border-secondary-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
          <Link to="/policies">
            <Button variant="ghost" size="sm" className="mb-4" data-testid="back-to-policies-button">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Policies
            </Button>
          </Link>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">{policy.category_type}</Badge>
                {policy.is_popular && (
                  <Badge variant="default" className="bg-green-100 text-green-700 hover:bg-green-100">
                    Popular
                  </Badge>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold font-heading text-secondary-900 mb-2">
                {policy.name}
              </h1>
              <p className="text-lg text-secondary-600">{policy.provider_name}</p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-4">
              <div className="text-right">
                <p className="text-sm text-secondary-600 mb-1">Annual Premium</p>
                <p className="text-4xl font-bold font-mono text-primary-700">
                  {formatCurrency(policy.premium_yearly)}
                </p>
              </div>
              <Button
                onClick={() => addToCompare(policy)}
                disabled={isInCompare(policy.id)}
                data-testid="add-to-compare-button"
              >
                {isInCompare(policy.id) ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Added to Compare
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Add to Compare
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="w-full grid grid-cols-4" data-testid="policy-tabs">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="benefits">Benefits</TabsTrigger>
                <TabsTrigger value="exclusions">Exclusions</TabsTrigger>
                <TabsTrigger value="terms">Terms</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Policy Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-base text-secondary-700 leading-relaxed">
                      {policy.summary}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                      <div className="space-y-1">
                        <p className="text-sm text-secondary-600">Coverage Amount</p>
                        <p className="text-xl font-semibold text-secondary-900">
                          {formatCurrency(policy.coverage_amount)}
                        </p>
                      </div>
                      {policy.features.network_size && (
                        <div className="space-y-1">
                          <p className="text-sm text-secondary-600">Network Size</p>
                          <p className="text-xl font-semibold text-secondary-900">
                            {policy.features.network_size.toLocaleString()} hospitals
                          </p>
                        </div>
                      )}
                      {policy.features.claim_settlement_ratio && (
                        <div className="space-y-1">
                          <p className="text-sm text-secondary-600">Claim Settlement Ratio</p>
                          <p className="text-xl font-semibold text-secondary-900">
                            {policy.features.claim_settlement_ratio}%
                          </p>
                        </div>
                      )}
                      {policy.features.waiting_period_days && (
                        <div className="space-y-1">
                          <p className="text-sm text-secondary-600">Waiting Period</p>
                          <p className="text-xl font-semibold text-secondary-900">
                            {policy.features.waiting_period_days} days
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="benefits" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Key Benefits</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {policy.key_benefits && policy.key_benefits.length > 0 ? (
                      <ul className="space-y-3">
                        {policy.key_benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-base text-secondary-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-secondary-600">No specific benefits listed.</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="exclusions" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Exclusions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {policy.exclusions && policy.exclusions.length > 0 ? (
                      <ul className="space-y-3">
                        {policy.exclusions.map((exclusion, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                            <span className="text-base text-secondary-700">{exclusion}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-secondary-600">No specific exclusions listed.</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="terms" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Terms & Conditions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-base text-secondary-700 leading-relaxed whitespace-pre-line">
                      {policy.terms || 'Standard terms and conditions apply. Please contact the provider for detailed policy documents.'}
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Summary */}
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Quick Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-secondary-200">
                    <span className="text-sm text-secondary-600">Premium</span>
                    <span className="text-base font-semibold text-secondary-900">
                      {formatCurrency(policy.premium_yearly)}/yr
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-secondary-200">
                    <span className="text-sm text-secondary-600">Coverage</span>
                    <span className="text-base font-semibold text-secondary-900">
                      {formatCurrency(policy.coverage_amount)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-secondary-200">
                    <span className="text-sm text-secondary-600">Cashless</span>
                    {policy.features.cashless ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-secondary-400" />
                    )}
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-secondary-600">Pre-existing</span>
                    {policy.features.pre_existing ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-secondary-400" />
                    )}
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={() => addToCompare(policy)}
                  disabled={isInCompare(policy.id)}
                  data-testid="add-to-compare-sidebar-button"
                >
                  {isInCompare(policy.id) ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Added to Compare
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Add to Compare
                    </>
                  )}
                </Button>

                <Link to="/compare" className="block">
                  <Button variant="outline" className="w-full" data-testid="view-comparison-button">
                    View Comparison
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyDetailPage;
